import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { inr } from "@/lib/api";
import { PageHeader, Panel } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, ChevronLeft, ChevronRight, Play, Rocket, Trash2, Plus } from "lucide-react";

const STEPS = [
  "Identity", "Org Coverage", "Customer Eligibility", "Source / Txn / Product",
  "Targets", "Slabs & Rates", "Formula & Exceptions", "Review & Simulate",
];

export default function SchemeBuilder() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = !!id;
  const [step, setStep] = useState(0);
  const [sim, setSim] = useState(null);
  const { data: existing } = useQuery({
    queryKey: ["scheme", id],
    queryFn: async () => (await api.get(`/schemes/${id}`)).data,
    enabled: isEdit,
  });
  const { data: masters } = useQuery({
    queryKey: ["sb-masters"],
    queryFn: async () => {
      const [opcos, regions, rsos, aos] = await Promise.all([
        api.get("/master/opcos"), api.get("/master/regions"),
        api.get("/master/rsos"), api.get("/master/aos"),
      ]);
      return { opcos: opcos.data.items, regions: regions.data.items,
               rsos: rsos.data.items, aos: aos.data.items };
    },
  });

  const [form, setForm] = useState({
    name: "", code: "", opco_id: "", region_id: "", rso_id: "", ao_id: "",
    coverage_level: "region", engine: "QD",
    period: "Q3 FY26", period_from: "", period_to: "",
    bags_per_mt: 20,
    eligibility: { dealer_status: ["active", "new", "revival"], min_target_mt: 30 },
    txn: { include_returns: false, product_category: "All" },
    targets: { source: "target-setting master", default_new_dealer_mt: 30 },
    slabs: [
      { from_mt: 0, to_mt: 500, rate_per_mt: 0 },
      { from_mt: 500, to_mt: 1000, rate_per_mt: 5 },
      { from_mt: 1000, to_mt: null, rate_per_mt: 5.5 },
    ],
    growth: { rate_per_mt: 425, base_target_mt: 300, previously_paid_monthly: 30000 },
    milestone: { block_size_mt: 25, bonus_per_block: 340, fallback_rate_per_mt: 60 },
    formula_notes: "final = engine_output - adjustments; negative delta → no recovery",
    approval_chain: ["Sales Review", "Finance Review", "Finance Approval"],
    sample_qty_mt: 1200,
  });

  useEffect(() => {
    if (masters?.opcos?.length && !form.opco_id) {
      setForm((f) => ({ ...f, opco_id: masters.opcos[0].id }));
    }
  }, [masters]);

  useEffect(() => {
    if (!existing) return;
    setForm((f) => ({
      ...f,
      name: existing.name || "", code: existing.code || "",
      opco_id: existing.opco_id || f.opco_id, region_id: existing.region_id || "",
      rso_id: existing.rso_id || "", ao_id: existing.ao_id || "",
      coverage_level: existing.coverage_level || "region",
      engine: existing.engine || "QD", period: existing.period || f.period,
      period_from: existing.period_from || "", period_to: existing.period_to || "",
      bags_per_mt: existing.engine === "QD" ? (existing.config?.bags_per_mt ?? f.bags_per_mt) : f.bags_per_mt,
      eligibility: existing.eligibility && Object.keys(existing.eligibility).length ? { ...f.eligibility, ...existing.eligibility } : f.eligibility,
      approval_chain: existing.approval_chain?.length ? existing.approval_chain : f.approval_chain,
      slabs: existing.engine === "QD" && existing.config?.slabs ? existing.config.slabs : f.slabs,
      growth: existing.engine === "GROWTH" ? { ...f.growth, ...existing.config } : f.growth,
      milestone: existing.engine === "MILESTONE" ? { ...f.milestone, ...existing.config } : f.milestone,
    }));
  }, [existing]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setSlab = (i, key, val) => {
    const slabs = [...form.slabs];
    slabs[i] = { ...slabs[i], [key]: val === "" ? null : Number(val) };
    set({ slabs });
  };

  const engineConfig = () => {
    if (form.engine === "QD") return { slabs: form.slabs, bags_per_mt: Number(form.bags_per_mt) || 1 };
    if (form.engine === "GROWTH") return { ...form.growth };
    return { ...form.milestone };
  };

  const runSim = async () => {
    try {
      const cfg = engineConfig();
      const { data } = await api.post("/schemes/simulate", {
        engine: form.engine, config: cfg, sample_qty_mt: Number(form.sample_qty_mt),
      });
      setSim(data.result);
      toast.success("Simulation complete");
    } catch (e) { toast.error(e.response?.data?.detail || "Simulation failed"); }
  };

  const publish = async () => {
    try {
      const payload = {
        name: form.name || "Untitled Scheme", code: form.code || null,
        opco_id: form.opco_id, region_id: form.region_id || null,
        rso_id: form.rso_id || null, ao_id: form.ao_id || null,
        coverage_level: form.coverage_level, engine: form.engine,
        period: (form.period_from && form.period_to) ? `${form.period_from} → ${form.period_to}` : form.period,
        period_from: form.period_from || null, period_to: form.period_to || null,
        config: engineConfig(),
        eligibility: form.eligibility, approval_chain: form.approval_chain,
      };
      if (isEdit) {
        await api.put(`/schemes/${id}`, payload);
        toast.success("Scheme updated");
      } else {
        const { data } = await api.post("/schemes", payload);
        toast.success(`Scheme ${data.code} created (draft)`);
      }
      qc.invalidateQueries({ queryKey: ["schemes"] });
      if (isEdit) qc.invalidateQueries({ queryKey: ["scheme", id] });
      nav("/schemes");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save scheme"); }
  };

  return (
    <div>
      <PageHeader title={isEdit ? "Edit Scheme" : "DIY Scheme Builder"}
        subtitle={isEdit ? "Update configuration · a new version is recorded on save"
          : "8-step guided builder · configure → simulate → publish"} />

      {/* Stepper */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2 mb-6" data-testid="scheme-stepper">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => setStep(i)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap border ${
              i === step ? "bg-[#0F2C59] text-white border-[#0F2C59]"
              : i < step ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-white text-slate-500 border-slate-200"}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${i === step ? "bg-white/20" : i < step ? "bg-emerald-200" : "bg-slate-100"}`}>
              {i < step ? <Check size={12} /> : i + 1}
            </span>
            {s}
          </button>
        ))}
      </div>

      <Panel testid="scheme-builder-panel" title={`Step ${step + 1}: ${STEPS[step]}`}>
        {step === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Div label="Scheme Name"><Input data-testid="sb-name" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. ACC Monsoon Volume Bonanza" /></Div>
            <Div label="Scheme Code (optional)"><Input data-testid="sb-code" value={form.code} onChange={(e) => set({ code: e.target.value })} placeholder="Auto-generated if blank" /></Div>
            <Div label="Period — From"><Input type="date" data-testid="sb-period-from" value={form.period_from} onChange={(e) => set({ period_from: e.target.value })} /></Div>
            <Div label="Period — To"><Input type="date" data-testid="sb-period-to" value={form.period_to} onChange={(e) => set({ period_to: e.target.value })} /></Div>
            <Div label="Engine">
              <select data-testid="sb-engine" className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                value={form.engine} onChange={(e) => set({ engine: e.target.value })}>
                <option value="QD">Quantity Discount (QD)</option>
                <option value="GROWTH">Growth (monthly + quarterly)</option>
                <option value="MILESTONE">Milestone (blocks + fallback)</option>
              </select>
            </Div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Div label="Operating Company">
                <select data-testid="sb-opco" className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                  value={form.opco_id} onChange={(e) => set({ opco_id: e.target.value, region_id: "", rso_id: "", ao_id: "" })}>
                  {masters?.opcos?.map((o) => <option key={o.id} value={o.id}>{o.code} — {o.name}</option>)}
                </select>
              </Div>
              <Div label="Coverage Level">
                <select data-testid="sb-coverage-level" className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                  value={form.coverage_level} onChange={(e) => set({ coverage_level: e.target.value, rso_id: "", ao_id: "" })}>
                  <option value="region">Region / Cluster level</option>
                  <option value="rso">RSO level</option>
                  <option value="rso_ao">RSO – AO level</option>
                </select>
              </Div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Div label="Region">
                <select data-testid="sb-region" className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                  value={form.region_id} onChange={(e) => set({ region_id: e.target.value, rso_id: "", ao_id: "" })}>
                  <option value="">{form.coverage_level === "region" ? "All regions" : "Select region"}</option>
                  {masters?.regions?.filter((r) => r.opco_id === form.opco_id).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </Div>
              {(form.coverage_level === "rso" || form.coverage_level === "rso_ao") && (
                <Div label="RSO">
                  <select data-testid="sb-rso" className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                    value={form.rso_id} onChange={(e) => set({ rso_id: e.target.value, ao_id: "" })}>
                    <option value="">Select RSO</option>
                    {masters?.rsos?.filter((r) => (!form.region_id || r.region_id === form.region_id) && r.opco_id === form.opco_id).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </Div>
              )}
              {form.coverage_level === "rso_ao" && (
                <Div label="AO">
                  <select data-testid="sb-ao" className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                    value={form.ao_id} onChange={(e) => set({ ao_id: e.target.value })}>
                    <option value="">Select AO</option>
                    {masters?.aos?.filter((a) => (!form.rso_id || a.rso_id === form.rso_id)).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </Div>
              )}
            </div>
            <div className="text-xs text-slate-400">Define scheme scope at Region/Cluster, RSO, or RSO–AO level. Backend access is enforced by role and geography.</div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="text-sm text-slate-500">Eligible dealer lifecycle states:</div>
            <div className="flex flex-wrap gap-2">
              {["active", "new", "revival", "inactive"].map((st) => {
                const on = form.eligibility.dealer_status.includes(st);
                return (
                  <button key={st} data-testid={`sb-status-${st}`}
                    onClick={() => {
                      const arr = on ? form.eligibility.dealer_status.filter((x) => x !== st) : [...form.eligibility.dealer_status, st];
                      set({ eligibility: { ...form.eligibility, dealer_status: arr } });
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm border ${on ? "bg-[#00529B] text-white border-[#00529B]" : "bg-white text-slate-600 border-slate-200"}`}>
                    {st}
                  </button>
                );
              })}
            </div>
            <Div label="Minimum target (MT) to qualify" className="max-w-xs">
              <Input type="number" value={form.eligibility.min_target_mt}
                onChange={(e) => set({ eligibility: { ...form.eligibility, min_target_mt: Number(e.target.value) } })} />
            </Div>
          </div>
        )}

        {step === 3 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Div label="Product category">
              <select className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                value={form.txn.product_category} onChange={(e) => set({ txn: { ...form.txn, product_category: e.target.value } })}>
                {["All", "OPC", "PPC", "PSC", "Composite"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Div>
            <Div label="Include returns in achievement?">
              <select className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                value={String(form.txn.include_returns)} onChange={(e) => set({ txn: { ...form.txn, include_returns: e.target.value === "true" } })}>
                <option value="false">No — net of returns</option>
                <option value="true">Yes</option>
              </select>
            </Div>
            <div className="sm:col-span-2 text-xs text-slate-400">Bags-per-MT is always resolved from the UoM/SKU master — never hard-coded.</div>
          </div>
        )}

        {step === 4 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Div label="Target source"><Input value={form.targets.source} readOnly className="bg-slate-50" /></Div>
            <Div label="New/revival dealer default target (MT)">
              <Input type="number" value={form.targets.default_new_dealer_mt}
                onChange={(e) => set({ targets: { ...form.targets, default_new_dealer_mt: Number(e.target.value) } })} />
            </Div>
            <div className="sm:col-span-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Assumption (configurable): base target sourced from target-setting master, not 3-month/YTD average.
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            {form.engine === "QD" && (
              <div className="space-y-2">
                <Div label="Bags per MT (from UoM master)" className="max-w-xs mb-3">
                  <Input type="number" data-testid="sb-bags" value={form.bags_per_mt}
                    onChange={(e) => set({ bags_per_mt: e.target.value === "" ? "" : Number(e.target.value) })} />
                </Div>
                <div className="text-xs text-slate-500 mb-2">Gross = Qty (MT) × Rate (₹/bag) × Bags per MT, as per the falling slab.</div>
                <div className="grid grid-cols-4 gap-2 text-xs uppercase tracking-wider text-slate-400 font-semibold px-1">
                  <span>From MT</span><span>To MT (blank = open)</span><span>Rate ₹/bag</span><span></span>
                </div>
                {form.slabs.map((s, i) => (
                  <div key={i} className="grid grid-cols-4 gap-2 items-center" data-testid={`sb-slab-${i}`}>
                    <Input type="number" value={s.from_mt ?? ""} onChange={(e) => setSlab(i, "from_mt", e.target.value)} />
                    <Input type="number" value={s.to_mt ?? ""} onChange={(e) => setSlab(i, "to_mt", e.target.value)} />
                    <Input type="number" value={s.rate_per_mt ?? ""} onChange={(e) => setSlab(i, "rate_per_mt", e.target.value)} />
                    <Button variant="ghost" size="icon" onClick={() => set({ slabs: form.slabs.filter((_, x) => x !== i) })}>
                      <Trash2 size={15} className="text-slate-400" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="gap-1.5 mt-1" data-testid="sb-add-slab"
                  onClick={() => set({ slabs: [...form.slabs, { from_mt: 0, to_mt: null, rate_per_mt: 0 }] })}>
                  <Plus size={14} /> Add slab
                </Button>
              </div>
            )}
            {form.engine === "GROWTH" && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Div label="Rate ₹/MT"><Input type="number" value={form.growth.rate_per_mt} onChange={(e) => set({ growth: { ...form.growth, rate_per_mt: Number(e.target.value) } })} /></Div>
                <Div label="Base target MT"><Input type="number" value={form.growth.base_target_mt} onChange={(e) => set({ growth: { ...form.growth, base_target_mt: Number(e.target.value) } })} /></Div>
                <Div label="Previously paid monthly ₹"><Input type="number" value={form.growth.previously_paid_monthly} onChange={(e) => set({ growth: { ...form.growth, previously_paid_monthly: Number(e.target.value) } })} /></Div>
              </div>
            )}
            {form.engine === "MILESTONE" && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Div label="Block size MT"><Input type="number" value={form.milestone.block_size_mt} onChange={(e) => set({ milestone: { ...form.milestone, block_size_mt: Number(e.target.value) } })} /></Div>
                <Div label="Bonus per block ₹"><Input type="number" value={form.milestone.bonus_per_block} onChange={(e) => set({ milestone: { ...form.milestone, bonus_per_block: Number(e.target.value) } })} /></Div>
                <Div label="Fallback rate ₹/MT"><Input type="number" value={form.milestone.fallback_rate_per_mt} onChange={(e) => set({ milestone: { ...form.milestone, fallback_rate_per_mt: Number(e.target.value) } })} /></Div>
              </div>
            )}
          </div>
        )}

        {step === 6 && (
          <div className="space-y-4">
            <Div label="Formula & exception notes">
              <Textarea value={form.formula_notes} onChange={(e) => set({ formula_notes: e.target.value })} rows={3} />
            </Div>
            <Div label="Approval chain (comma-separated)">
              <Input value={form.approval_chain.join(", ")} onChange={(e) => set({ approval_chain: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
            </Div>
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Missing rate/target for an eligible dealer is routed to the Exception Workbench — never silently zeroed.
            </div>
          </div>
        )}

        {step === 7 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <Div label="Sample achievement (MT) to simulate" className="max-w-xs mb-4">
                <Input type="number" data-testid="sb-sim-qty" value={form.sample_qty_mt} onChange={(e) => set({ sample_qty_mt: e.target.value })} />
              </Div>
              <Button data-testid="sb-simulate-btn" onClick={runSim} className="gap-2 bg-[#00529B] hover:bg-[#0F2C59]">
                <Play size={15} /> Run Simulation
              </Button>
              <div className="mt-6 text-sm space-y-1.5 text-slate-600">
                <div><b>Name:</b> {form.name || "—"}</div>
                <div><b>Engine:</b> {form.engine}</div>
                <div><b>Approval chain:</b> {form.approval_chain.join(" → ")}</div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="sb-sim-result">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Simulation Result</div>
              {sim ? (
                <div className="space-y-2">
                  {Object.entries(sim).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm border-b border-slate-200 py-1.5 last:border-0">
                      <span className="text-slate-500">{k}</span>
                      <span className="font-medium text-slate-800 text-right">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center rounded-lg bg-[#0F2C59] text-white p-3 mt-3">
                    <span className="text-sm text-white/70">Computed incentive</span>
                    <span className="font-heading text-xl font-bold">{inr(sim.final_amount)}</span>
                  </div>
                </div>
              ) : <div className="text-sm text-slate-400 py-8 text-center">Run a simulation to preview the computed payout.</div>}
            </div>
          </div>
        )}
      </Panel>

      <div className="flex justify-between mt-6">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="gap-2" data-testid="sb-prev-btn">
          <ChevronLeft size={16} /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={() => setStep((s) => s + 1)} className="gap-2 bg-[#0F2C59] hover:bg-[#00529B]" data-testid="sb-next-btn">
            Next <ChevronRight size={16} />
          </Button>
        ) : (
          <Button onClick={publish} className="gap-2 bg-[#D97706] hover:bg-[#F59E0B]" data-testid="sb-publish-btn">
            <Rocket size={16} /> {isEdit ? "Update Scheme" : "Create Scheme"}
          </Button>
        )}
      </div>
    </div>
  );
}

function Div({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-slate-700 text-sm">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
